import { useEffect } from "react";
import { Col, Row } from "antd";
import { useNavigate, useParams } from "react-router-dom";

import { useTranslation } from "contexts/TranslationContext";
import BackButton from "shared/components/BackButton/BackButton";
import routes, { appRoute } from "shared/constants/routes";
import Arrow from "assets/svg/arrow.svg";
import {
  getServiceById,
  getServiceDetail,
  serviceName,
  pickLang,
} from "components/Home/OtherServices/otherServicesData";
import FinanceServiceFlow from "./FinanceServiceFlow";
import ServiceEmptyState from "./ServiceEmptyState";

// Reusable "first screen" for the Other Services that have further steps
// (Loan Restructure, Topup, Early/Partial Settlement, Loan Cancelation, Buy-Out,
// Clearance Letter, Debt Proof Letter). Left column = product info (same layout
// as FinanceProducts / DentalProductsV2), right column = a "not available" empty
// state whose copy varies per product. All content is data-driven from
// otherServicesData (hardcoded now, backend later).
const ServiceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { language } = useTranslation();
  const isArabic = language?.code === "ar";

  const item = getServiceById(id);
  const detail = getServiceDetail(id);

  // Guard: unknown id or a service without a detail page (e.g. a Coming Soon
  // one reached by a hand-typed URL) → send back to the list.
  useEffect(() => {
    if (!item || !detail) {
      navigate(appRoute(routes.OTHER_SERVICES + "/list"), { replace: true });
    }
  }, [item, detail, navigate]);

  if (!item || !detail) return null;

  const title = serviceName(item, isArabic);
  const heading = detail.heading ? pickLang(detail.heading, isArabic) : title;
  const subtitle = pickLang(detail.subtitle, isArabic);
  const description = pickLang(detail.description, isArabic);
  const bullets = (detail.bullets || []).map((b) => pickLang(b, isArabic));

  return (
    <div className="rounded-xl mb-4 px-4 py-4 bg-white shadow-lg">
      <div className="p-5">
        <BackButton onClick={() => navigate(-1)} />

        <Row gutter={[16, 16]} className="pt-4">
          {/* LEFT — product info */}
          <Col xs={24} md={12}>
            {/* Banner (text + product image) — same treatment as FinanceProducts */}
            <div className="w-full max-w-[530px] overflow-hidden">
              <div className="bg-[#E6F1F2] rounded-lg overflow-hidden flex flex-row">
                <div className="p-6 flex flex-col justify-center flex-1">
                  <h3 className="text-[#1F225B] text-xl md:text-2xl font-light mb-2">
                    {title}
                  </h3>
                  <p className="text-[#8E9196] text-sm md:text-base">{subtitle}</p>
                </div>
                <div className="md:w-2/5 sm:w-2/5 w-1/2">
                  {detail.bannerImage ? (
                    <img
                      src={detail.bannerImage}
                      alt={title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    // Product image placeholder — provided later; set detail.bannerImage.
                    <div className="w-full h-full min-h-[150px] bg-[#D6E8EA]" />
                  )}
                </div>
              </div>
            </div>

            {/* Heading + description + eligibility bullets */}
            <div className="max-w-[550px] mt-5">
              <h2 className="text-[#1F1F1F] text-2xl font-semibold mb-3">{heading}</h2>
              <p className="text-[#878787] text-base md:text-lg font-light text-start mb-4">
                {description}
              </p>
              <ul className="mt-3">
                {bullets.map((text, index) => (
                  <li className="flex mb-4 gap-2" key={index}>
                    <span className="flex-shrink-0">
                      <img src={Arrow} alt="checkmark" className="w-6 h-6" />
                    </span>
                    <span className="text-sm break-words">{text}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Col>

          {/* RIGHT — Restructure/Topup flow, or the "not available" empty state */}
          <Col xs={24} md={12}>
            {detail.flow ? (
              <FinanceServiceFlow detail={detail} isArabic={isArabic} />
            ) : (
              <ServiceEmptyState
                emptyState={detail.emptyState}
                isArabic={isArabic}
                image={detail.emptyStateImage}
              />
            )}
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default ServiceDetail;
